/**
 * @ComponentFor EPiServer.Core.XhtmlString
 */
import * as React from 'react';
import { connect } from 'react-redux';
import { State, isBrowser, Link, loadScript, unloadScript, resolveSyncIfLoadedOrThrow } from '@avensia/scope';
import { supportsComponentProp } from '../EpiProperty';
import * as inlineScripts from '@avensia/scope/inline-scripts';

type HtmlDecorator = (
  item: any,
  i: number,
  props: any,
  componentProps: any,
  children: React.ReactNode | React.ReactNode[],
) => any;
const decorators: { [tag: string]: HtmlDecorator[] } = {};
export function registerDecorator(tag: string, decorator: HtmlDecorator) {
  if (!decorators[tag]) {
    decorators[tag] = [];
  }
  decorators[tag].push(decorator);

  return () => {
    if (decorators[tag]) {
      const index = decorators[tag].indexOf(decorator);
      if (index !== -1) {
        decorators[tag].splice(index, 1);
      }
    }
  };
}

const allowedEvents = [
  'onclick',
  'onblur',
  'onkeydown',
  'onkeyup',
  'onkeypress',
  'onfocus',
  'onmouseover',
  'onmouseout',
  'onmouseup',
  'onmousedown',
  'onscroll',
  'onchange',
  'onsubmit',
  'onload',
  'onresize',
];

function htmlEventToReactEventName(htmlEvent: string) {
  return htmlEvent.substr(0, 2) + htmlEvent[2].toUpperCase() + htmlEvent.substr(3);
}

function isElement(item: Scope.XhtmlStringContent): item is Scope.XhtmlStringElement {
  return !!(item as Scope.XhtmlStringElement).tag;
}
function isComponent(item: Scope.XhtmlStringContent): item is Scope.XhtmlStringComponent {
  return !!(item as Scope.XhtmlStringComponent).componentName;
}

export function renderHtmlTag(
  item: Scope.XhtmlStringContent,
  index: number,
  componentProps: any,
  currentTheme?: string,
): React.ReactNode {
  if (!item) {
    return null;
  }

  if (isElement(item)) {
    const attr = Object.assign({ key: index }, item.attr);

    const relevantDecorators = (decorators['*'] || []).concat(decorators[item.tag] || []);
    for (const decorator of relevantDecorators) {
      let children: any;
      if (Array.isArray(item.children)) {
        children = item.children.map((child: any, i: number) => renderHtmlTag(child, i, componentProps, currentTheme));
      } else {
        children = item.children;
      }
      const result = decorator(item, index, attr, componentProps, children);
      if (result) {
        console.info('Decorator handled rendering for', item);
        return result;
      }
    }

    allowedEvents.forEach(allowedEvent => {
      if (attr[allowedEvent]) {
        const attrWithFn = attr as { [attr: string]: string | Function };
        const stringHandler = attr[allowedEvent];
        attrWithFn[htmlEventToReactEventName(allowedEvent)] = (event: Event, ...args: any[]) => {
          // If CMS contains javascript errors we don't want to fail execution
          try {
            new Function(stringHandler).apply(event.target, [event, ...args]);
          } catch (e) {
            window.console.error(e);
          }
        };
        delete attr[allowedEvent];
      }
    });
    if (item.tag === 'a') {
      let children: React.ReactNode[];
      if (Array.isArray(item.children)) {
        children = item.children.map((child: any, i: number) => renderHtmlTag(child, i, componentProps, currentTheme));
      } else {
        children = item.children;
      }
      return (
        <Link {...attr} to={attr.href || ''}>
          {children}
        </Link>
      );
    } else if (item.tag === 'style') {
      // https://github.com/facebook/react/issues/7093
      return React.createElement(item.tag, attr, item.children[0]);
    } else if (item.tag === 'script') {
      if (isBrowser()) {
        return (
          <noscript
            key={attr.key}
            ref={el => {
              const anyEl = el as any;
              if (el) {
                if (item.children && !anyEl.loaded) {
                  anyEl.loaded = true;
                  try {
                    console.info('Evaluating inline script', item.children[0]);
                    eval(item.children[0] as string);
                  } catch (e) {
                    console.error(e);
                  }

                  // Add this script toupled with the domElement to a list to be able to eval it again
                  // on history changes where the element is not created, but re-rendered
                  if (!inlineScripts.elementExists(el)) {
                    inlineScripts.addScript({
                      domElement: el,
                      firstRun: true, // push-loaded will be fired after this ran, we need to make sure not to eval it twice on the same load
                      script: item.children[0] as string,
                    });
                  }
                } else if (attr.src && !anyEl.loaded) {
                  anyEl.loaded = true;
                  console.info('Loading script', attr.src);
                  loadScript(getUrl(attr.src));
                }
              } else if (attr.src) {
                console.info('Unloading script', attr.src);
                unloadScript(getUrl(attr.src));
              }
            }}
          />
        );
      } else {
        // We don't want to include a script tag when running on the server because that will cause the script
        // to load once again when the client dehydrates the render tree. This has the slight disadvantage that
        // scripts will load first when everything else is up and ready and not when the browser parses the initial
        // server rendered DOM, but it's much simpler this way.
        return <noscript key={attr.key} />;
      }
    } else if (item.tag === 'img') {
      attr.src = getUrl(attr.src);
      return React.createElement(
        item.tag,
        attr,
        item.children &&
          item.children.map((childItem: any, j: number) => renderHtmlTag(childItem, j, componentProps, currentTheme)),
      );
    } else {
      return React.createElement(
        item.tag,
        attr,
        item.children &&
          item.children.map((childItem: any, j: number) => renderHtmlTag(childItem, j, componentProps, currentTheme)),
      );
    }
  } else if (isComponent(item)) {
    const Component = resolveSyncIfLoadedOrThrow(item.componentName, currentTheme);
    const model = Object.assign({ key: index }, item);
    return React.createElement(Component as any, model);
  } else {
    return item;
  }
}

function getUrl(url: string | Scope.UrlViewModel) {
  if (!url) {
    return '';
  }
  if (typeof url === 'string') {
    return url;
  }
  return url.url;
}

export type HtmlProps = Scope.XhtmlString & {
  component?: React.ComponentType<any> | string;
  className?: string;
  propertyName?: string;
};

type ConnectedProps = {
  currentTheme: string;
};

export default supportsComponentProp(
  connect(
    (state: State): ConnectedProps => ({
      currentTheme: state.currentTheme,
    }),
  )(({ component: Component = 'div', className = '', currentTheme, ...restProps }: HtmlProps & ConnectedProps) => (
    <Component className={className}>
      {restProps.html && restProps.html.map((item, i) => renderHtmlTag(item, i, restProps, currentTheme))}
    </Component>
  )),
);
